import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import logger from '../../lib/logger';
import {
  MessageSquare, Send, Check, Trash2, CornerDownRight,
  Loader2, RefreshCw, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SECTION_LABELS = {
  executive_summary: 'Executive Summary',
  cost_breakdown: 'Cost & Effort Analysis',
  component_details: 'Component Details',
  risk_analysis: 'Risk & Feasibility',
  implementation_timeline: 'Implementation Timeline',
};

function Initials({ name }) {
  const initials = (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div className="w-6 h-6 rounded-full bg-[#0F2040] text-white flex items-center justify-center text-[9px] font-bold shrink-0">
      {initials}
    </div>
  );
}

function timeAgo(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function WorkspaceCommentsTab() {
  const { estimateId } = useOutletContext();
  const [comments, setComments] = useState([]);
  const [sectionCounts, setSectionCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [newText, setNewText] = useState('');
  const [newSection, setNewSection] = useState('executive_summary');
  const [posting, setPosting] = useState(false);
  const [expandedSections, setExpandedSections] = useState(new Set(['executive_summary']));
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState('');

  const fetchComments = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/estimates/${estimateId}/comments`, { withCredentials: true });
      setComments(res.data.comments || []);
      setSectionCounts(res.data.section_counts || {});
    } catch (err) {
      logger.error('Failed to fetch comments:', err);
    } finally {
      setLoading(false);
    }
  }, [estimateId]);

  useEffect(() => { fetchComments(); }, [fetchComments]);

  const handlePost = useCallback(async () => {
    if (!newText.trim()) return;
    setPosting(true);
    try {
      await axios.post(
        `${API_URL}/api/estimates/${estimateId}/comments`,
        { section: newSection, text: newText.trim() },
        { withCredentials: true }
      );
      setNewText('');
      toast.success('Comment added');
      fetchComments();
    } catch (err) {
      toast.error('Failed to post comment');
    } finally {
      setPosting(false);
    }
  }, [estimateId, newSection, newText, fetchComments]);

  const handleReply = useCallback(async (parentId, section) => {
    if (!replyText.trim()) return;
    setPosting(true);
    try {
      await axios.post(
        `${API_URL}/api/estimates/${estimateId}/comments`,
        { section, text: replyText.trim(), parent_id: parentId },
        { withCredentials: true }
      );
      setReplyTo(null);
      setReplyText('');
      toast.success('Reply added');
      fetchComments();
    } catch (err) {
      toast.error('Failed to post reply');
    } finally {
      setPosting(false);
    }
  }, [estimateId, replyText, fetchComments]);

  const handleResolve = useCallback(async (commentId) => {
    try {
      await axios.patch(
        `${API_URL}/api/estimates/${estimateId}/comments/${commentId}/resolve`,
        {},
        { withCredentials: true }
      );
      fetchComments();
    } catch (err) {
      toast.error('Failed to resolve');
    }
  }, [estimateId, fetchComments]);

  const handleDelete = useCallback(async (commentId) => {
    try {
      await axios.delete(
        `${API_URL}/api/estimates/${estimateId}/comments/${commentId}`,
        { withCredentials: true }
      );
      toast.success('Comment deleted');
      fetchComments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  }, [estimateId, fetchComments]);

  const toggleSection = (section) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  };

  // Group comments by section
  const commentsBySection = {};
  comments.forEach(c => {
    const s = c.section || 'other';
    if (!commentsBySection[s]) commentsBySection[s] = [];
    commentsBySection[s].push(c);
  });

  const totalComments = comments.length;
  const unresolvedCount = comments.filter(c => !c.resolved && !c.parent_id).length;

  if (loading) {
    return (
      <div className="w-full px-4 sm:px-6 lg:px-8 py-12 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#4ABFCF]" />
      </div>
    );
  }

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6" data-testid="workspace-comments">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-slate-400" />
          <h2 className="text-sm font-semibold text-[#0F2040]">Comments</h2>
          <Badge variant="secondary" className="text-[9px]">{totalComments}</Badge>
          {unresolvedCount > 0 && (
            <Badge className="bg-amber-100 text-amber-700 text-[9px]">{unresolvedCount} open</Badge>
          )}
        </div>
        <button
          onClick={fetchComments}
          className="flex items-center gap-1 text-[10px] text-slate-500 hover:text-[#4ABFCF] transition-colors"
          data-testid="comments-refresh-btn"
        >
          <RefreshCw className="w-3 h-3" />
          Refresh
        </button>
      </div>

      {/* New Comment Form */}
      <Card className="border-slate-200 mb-6">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <label className="text-[10px] text-slate-500 font-medium">Section:</label>
            <select
              value={newSection}
              onChange={(e) => setNewSection(e.target.value)}
              className="text-[10px] border border-slate-200 rounded-md px-2 py-1 text-slate-700"
              data-testid="comment-section-select"
            >
              {Object.entries(SECTION_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handlePost()}
              placeholder="Add a comment..."
              className="flex-1 text-xs border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#4ABFCF]"
              data-testid="comment-input"
            />
            <Button
              size="sm"
              onClick={handlePost}
              disabled={posting || !newText.trim()}
              className="bg-[#0F2040] hover:bg-[#0F2040]/90"
              data-testid="comment-submit-btn"
            >
              <Send className="w-3.5 h-3.5" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Comments by Section */}
      {totalComments === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-12 text-center">
            <MessageSquare className="w-8 h-8 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No comments yet. Start a discussion!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {Object.entries(SECTION_LABELS).map(([sectionKey, sectionLabel]) => {
            const sectionComments = commentsBySection[sectionKey] || [];
            if (sectionComments.length === 0) return null;
            const isExpanded = expandedSections.has(sectionKey);
            const unresolved = sectionCounts[sectionKey] || 0;

            // Separate top-level and replies
            const topLevel = sectionComments.filter(c => !c.parent_id);
            const replies = {};
            sectionComments.filter(c => c.parent_id).forEach(c => {
              if (!replies[c.parent_id]) replies[c.parent_id] = [];
              replies[c.parent_id].push(c);
            });

            return (
              <Card key={sectionKey} className="border-slate-200">
                <button
                  onClick={() => toggleSection(sectionKey)}
                  className="w-full flex items-center justify-between p-3 hover:bg-slate-50 transition-colors rounded-t-lg"
                  data-testid={`section-toggle-${sectionKey}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-[#0F2040]">{sectionLabel}</span>
                    <Badge variant="secondary" className="text-[9px]">{sectionComments.length}</Badge>
                    {unresolved > 0 && (
                      <Badge className="bg-amber-100 text-amber-700 text-[9px]">{unresolved} open</Badge>
                    )}
                  </div>
                  {isExpanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
                </button>

                {isExpanded && (
                  <CardContent className="px-3 pb-3 pt-0 space-y-3">
                    {topLevel.map((comment) => (
                      <div
                        key={comment.id}
                        className={`rounded-lg border p-3 ${comment.resolved ? 'bg-slate-50 border-slate-200 opacity-60' : 'bg-white border-slate-200'}`}
                        data-testid={`comment-${comment.id}`}
                      >
                        <div className="flex items-start gap-2">
                          <Initials name={comment.author_name} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-semibold text-[#0F2040]">{comment.author_name}</span>
                              <span className="text-[9px] text-slate-400">{timeAgo(comment.created_at)}</span>
                              {comment.resolved && <Badge className="bg-green-100 text-green-700 text-[8px] py-0">Resolved</Badge>}
                            </div>
                            <p className={`text-xs text-slate-700 mt-0.5 ${comment.resolved ? 'line-through' : ''}`}>
                              {comment.text}
                            </p>
                            <div className="flex items-center gap-2 mt-1.5">
                              <button
                                onClick={() => setReplyTo(replyTo === comment.id ? null : comment.id)}
                                className="text-[9px] text-slate-400 hover:text-[#4ABFCF] transition-colors flex items-center gap-0.5"
                              >
                                <CornerDownRight className="w-2.5 h-2.5" /> Reply
                              </button>
                              <button
                                onClick={() => handleResolve(comment.id)}
                                className="text-[9px] text-slate-400 hover:text-green-600 transition-colors flex items-center gap-0.5"
                              >
                                <Check className="w-2.5 h-2.5" /> {comment.resolved ? 'Reopen' : 'Resolve'}
                              </button>
                              <button
                                onClick={() => handleDelete(comment.id)}
                                className="text-[9px] text-slate-400 hover:text-red-600 transition-colors flex items-center gap-0.5"
                              >
                                <Trash2 className="w-2.5 h-2.5" /> Delete
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Replies */}
                        {(replies[comment.id] || []).map((reply) => (
                          <div key={reply.id} className="ml-8 mt-2 pl-3 border-l-2 border-slate-100" data-testid={`reply-${reply.id}`}>
                            <div className="flex items-start gap-2">
                              <Initials name={reply.author_name} />
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-[10px] font-semibold text-[#0F2040]">{reply.author_name}</span>
                                  <span className="text-[9px] text-slate-400">{timeAgo(reply.created_at)}</span>
                                </div>
                                <p className="text-xs text-slate-700 mt-0.5">{reply.text}</p>
                              </div>
                            </div>
                          </div>
                        ))}

                        {/* Reply input */}
                        {replyTo === comment.id && (
                          <div className="ml-8 mt-2 flex gap-2">
                            <input
                              type="text"
                              value={replyText}
                              onChange={(e) => setReplyText(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleReply(comment.id, comment.section)}
                              placeholder="Write a reply..."
                              className="flex-1 text-[10px] border border-slate-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#4ABFCF]"
                              autoFocus
                              data-testid={`reply-input-${comment.id}`}
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleReply(comment.id, comment.section)}
                              disabled={posting || !replyText.trim()}
                              className="h-6 px-2"
                            >
                              <Send className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
